/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {callOpenRouterApi} from '../../services/openrouterApi';
import {UserProfile, ChatMessage as ChatMessageType} from '../../types';
import {getTheoryExamPrompt} from '../../utils/prompts';
import {supabase} from '../../services/supabase';
import ChatMessage from '../common/ChatMessage';
import InputField from '../common/InputField';
import MarkdownRenderer from '../common/MarkdownRenderer';
import {useTTS} from '../../contexts/TTSContext';
import FeedbackDashboard from './FeedbackDashboard';
import '../../styles/components/TheoryExamSimulator.css';

interface Course {
  id: string;
  course_name: string;
  course_code: string;
  syllabus: string;
  lecturer_keywords: string[];
}

interface TheorySubQuestion {
  label: string;
  text: string;
  marks: number;
  expectedVerb: 'define' | 'explain' | 'describe' | 'list' | 'differentiate' | 'discuss' | 'calculate' | 'compare';
}

interface TheoryQuestion {
  mainQuestionNumber: number;
  subQuestions: TheorySubQuestion[];
  totalMarks: number;
  questionType: 'theory' | 'quantitative' | 'mixed';
  context?: string;
  modelAnswer: {
    subAnswers: Record<string, string>;
    markingScheme: Record<string, string[]>;
  };
}

interface TheoryAnswer {
  subAnswers: Record<string, string>;
  subSubmitted: Record<string, boolean>;
  submitted: boolean;
  submittedTime?: Date;
  marks?: {
    subMarks: Record<string, number>;
    total: number;
  };
  feedback?: {
    subFeedback: Record<string, string>;
    overall: string;
  };
}

interface ExamState {
  questions: TheoryQuestion[];
  currentQuestion: number;
  currentSubQuestion: string;
  answers: TheoryAnswer[];
  isComplete: boolean;
  totalMarks: number;
  startTime: Date;
  endTime: Date | null;
  showResults: boolean;
  isGrading: boolean;
  performance?: {
    score: number;
    percentage: number;
    readinessScore: number;
    predictedOutcome: "Fail Risk" | "Borderline" | "Likely Pass";
  };
  markingBreakdown?: Array<{
    questionNum: number;
    subLabel: string;
    criteria: string;
    maxScore: number;
    scoreAwarded: number;
    examinerExpectation: string;
    userPerformance: string;
    marksLostReason: string;
  }>;
  cognitiveAnalysis?: {
    structure: number;
    clarity: number;
    conceptDepth: number;
    blankRisk: number;
  };
  missingPoints?: string[];
  strongPoints?: string[];
  studyPlan?: {
    focusAreas: string[];
    actions: string[];
    estimatedImprovement: string;
  };
  retestPlan?: {
    recommended: boolean;
    instructions: string;
  };
}

interface ExamSession {
  id: string;
  title: string;
  timestamp: Date;
  messages: ChatMessageType[];
  examLevel: 'university';
  config: ExamConfig;
  isTemporary?: boolean;
  exam_completed?: boolean;
  exam_results?: string;
  total_marks?: number;
  obtained_marks?: number;
  total_questions?: number;
  time_taken_minutes?: number;
  integrity_metrics?: {
    copy_attempts?: number;
    paste_attempts?: number;
    tab_switches?: number;
    focus_lost_count?: number;
  };
  answers?: TheoryAnswer[];
  score_percentage?: number;
  feedback_data?: any;
}

interface ExamConfig {
  examLevel: 'university';
  questionCount: number;
  duration: number;
  showConfidenceCheck: boolean;
  selectedCourseId: string | null;
  selectedCourse: Course | null;
  university: {
    fieldOfStudy: string;
    level: 'Undergraduate' | 'Postgraduate' | 'Researcher';
    course: string;
    topics: string[];
    keywords: string[];
    questionTypes?: string[];
  };
}

interface TheoryExamSimulatorProps {
  userProfile: UserProfile;
  sessionId?: string;
}

const TheoryExamSimulator: React.FC<TheoryExamSimulatorProps> = ({userProfile, sessionId}) => {
  // State declarations
  const [sessions, setSessions] = useState<ExamSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  
  // Courses state
  const [userCourses, setUserCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  
  // Exam configuration states
  const [examConfig, setExamConfig] = useState<ExamConfig>({
    examLevel: 'university',
    questionCount: 4,
    duration: 120,
    showConfidenceCheck: true,
    selectedCourseId: null,
    selectedCourse: null,
    university: {
      fieldOfStudy: '',
      level: 'Undergraduate',
      course: '',
      topics: [],
      keywords: [],
      questionTypes: ['definition', 'explain', 'differentiate', 'with-examples', 'discuss']
    }
  });
  
  // Temporary state for number inputs
  const [tempQuestionCount, setTempQuestionCount] = useState<string>('4');
  const [tempDuration, setTempDuration] = useState<string>('120');
  
  // Exam-specific states
  const [examState, setExamState] = useState<ExamState | null>(null);
  const [examStarted, setExamStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [examIntegrity, setExamIntegrity] = useState({
    copyAttempts: 0,
    pasteAttempts: 0,
    tabSwitches: 0,
    lastFocusTime: Date.now(),
    lastTabSwitch: Date.now()
  });
  const [showConfidenceCheck, setShowConfidenceCheck] = useState(true);
  const [confidenceLevels, setConfidenceLevels] = useState<('High' | 'Medium' | 'Low' | null)[]>([]);

  // Chat functionality
  const [chatInput, setChatInput] = useState('');
  const {speak, isMessageSpeaking} = useTTS();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const isSwitchingSessionRef = useRef(false); // Prevent auto-reset during session switch

  // Load user courses
  useEffect(() => {
    loadUserCourses();
  }, []);

  const loadUserCourses = async () => {
    setLoadingCourses(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: courses, error } = await supabase
        .from('courses')
        .select('id, course_name, course_code, syllabus, lecturer_keywords')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setUserCourses(courses || []);
    } catch (error) {
      console.error('Error loading courses:', error);
      setErrorMessage('Failed to load your courses');
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleCourseSelect = (courseId: string) => {
    const selectedCourse = userCourses.find(c => c.id === courseId);
    if (selectedCourse) {
      let topics: string[] = [];
      let keywords: string[] = selectedCourse.lecturer_keywords || [];
      
      if (selectedCourse.syllabus) {
        topics = selectedCourse.syllabus
          .split(/[\n,]+/)
          .map(t => t.trim())
          .filter(t => t.length > 0);
      }
      
      setExamConfig(prev => ({
        ...prev,
        selectedCourseId: courseId,
        selectedCourse: selectedCourse,
        university: {
          ...prev.university,
          course: selectedCourse.course_name,
          topics: topics,
          keywords: keywords
        }
      }));
    }
  };

  useEffect(() => {
    setTempQuestionCount(examConfig.questionCount.toString());
    setTempDuration(examConfig.duration.toString());
  }, [examConfig]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    isMountedRef.current = true;
    
    const loadData = async () => {
      if (isMountedRef.current) {
        await loadSessions();
      }
    };
    
    loadData();
    
    const handleResize = () => {
      if (isMountedRef.current) {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const loadSessions = async () => {
    try {
      const {data: {user}, error: authError} = await supabase.auth.getUser();
      if (authError || !user) {
        console.log('User not authenticated');
        return;
      }

      const {data: sessionData, error: sessionError} = await supabase
        .from('exam_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('exam_type', 'theory-simulator')
        .order('created_at', {ascending: false});

      if (sessionError) {
        console.error('Error loading exam sessions:', sessionError);
        return;
      }

      const loadedSessions: ExamSession[] = [];
      
      for (const session of sessionData) {
        try {
          const {data: msgs, error: msgError} = await supabase
            .from('exam_messages')
            .select('*')
            .eq('session_id', session.id)
            .order('created_at', {ascending: true});

          let answers: TheoryAnswer[] = [];
          if (session.answers) {
            try {
              answers = typeof session.answers === 'string' 
                ? JSON.parse(session.answers) 
                : session.answers;
            } catch (e) {
              console.error('Error parsing answers:', e);
              answers = [];
            }
          }

          let feedbackData = null;
          if (session.feedback_data) {
            try {
              feedbackData = typeof session.feedback_data === 'string'
                ? JSON.parse(session.feedback_data)
                : session.feedback_data;
            } catch (e) {
              console.error('Error parsing feedback data:', e);
            }
          }

          let config = session.config;
          if (typeof config === 'string') {
            try {
              config = JSON.parse(config);
            } catch (e) {
              console.error('Error parsing config:', e);
              config = {};
            }
          }

          const sessionObj: ExamSession = {
            id: session.id,
            title: session.title || 'Theory Exam',
            timestamp: new Date(session.created_at),
            messages: msgs?.map(msg => ({
              id: msg.id,
              role: msg.role,
              content: msg.content,
              timestamp: new Date(msg.created_at),
              chat_type: 'theory-exam',
            })) || [],
            examLevel: 'university',
            config: config,
            exam_completed: session.exam_completed || false,
            exam_results: session.exam_results,
            total_marks: session.total_marks,
            obtained_marks: session.obtained_marks,
            total_questions: session.total_questions,
            time_taken_minutes: session.time_taken_minutes,
            integrity_metrics: session.integrity_metrics,
            answers: answers,
            score_percentage: session.score_percentage,
            feedback_data: feedbackData,
            isTemporary: false
          };
          
          loadedSessions.push(sessionObj);
        } catch (error) {
          console.error('Error processing session:', error);
        }
      }

      if (isMountedRef.current) {
        setSessions(loadedSessions);
        
        if (sessionId && loadedSessions.find(s => s.id === sessionId)) {
          switchToSession(sessionId);
        } else if (loadedSessions.length > 0 && !currentSessionId) {
          switchToSession(loadedSessions[0].id);
        }
      }
    } catch (error) {
      console.error('Error in loadSessions:', error);
    }
  };

  const createNewSession = async () => {
    const {data: {user}} = await supabase.auth.getUser();
    if (!user) {
      setErrorMessage('Please log in to create a new exam session');
      return null;
    }
    
    const title = 'New Theory Exam';
    const newSessionId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`;
    
    const {data: newSession, error} = await supabase
      .from('exam_sessions')
      .insert({
        id: newSessionId,
        user_id: user.id,
        title: title,
        exam_type: 'theory-simulator',
        exam_level: 'university',
        config: examConfig,
        question_count: examConfig.questionCount,
        duration_minutes: examConfig.duration,
        exam_completed: false,
        chat_type: 'theory-exam',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating session:', error);
      setErrorMessage('Failed to create new session');
      return null;
    }
    
    const sessionObj: ExamSession = {
      id: newSession.id,
      title: title,
      timestamp: new Date(newSession.created_at),
      messages: [],
      examLevel: 'university',
      config: examConfig,
      isTemporary: false,
      exam_completed: false,
    };
    
    if (isMountedRef.current) {
      setSessions(prev => [sessionObj, ...prev]);
      setCurrentSessionId(sessionObj.id);
      setMessages([]);
      setExamStarted(false);
      setExamState(null);
      setConfidenceLevels([]);
      setShowCorrectAnswers(false);
    }
    
    return sessionObj;
  };

  const saveChatMessage = async (sessionId: string, role: 'user' | 'assistant', content: string) => {
    if (!sessionId || sessionId.startsWith('temp-')) {
      return null;
    }

    try {
      const {data, error} = await supabase
        .from('exam_messages')
        .insert({
          session_id: sessionId,
          role: role,
          content: content,
          message_type: 'chat',
          created_at: new Date().toISOString(),
          metadata: {}
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving message:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in saveChatMessage:', error);
      return null;
    }
  };

  const updateExamSessionResults = async (sessionId: string, results: any) => {
    if (!sessionId || sessionId.startsWith('temp-')) return;

    try {
      const {error} = await supabase
        .from('exam_sessions')
        .update({
          ...results,
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (error) {
        console.error('Error updating exam results:', error);
      }
    } catch (error) {
      console.error('Error in updateExamSessionResults:', error);
    }
  };

  const updateSessionTitle = async (sessionId: string, title: string) => {
    if (!sessionId || sessionId.startsWith('temp-')) return;
    
    try {
      const {error} = await supabase
        .from('exam_sessions')
        .update({
          title: title,
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId);
      
      if (!error) {
        setSessions(prev => prev.map(session => 
          session.id === sessionId ? {...session, title: title} : session
        ));
      }
    } catch (error) {
      console.error('Error updating session title:', error);
    }
  };

  const safeJsonParse = (text: string) => {
    let cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '');
    
    const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      cleaned = jsonMatch[0];
    }
    
    let bracketCount = 0;
    let inString = false;
    let escapeNext = false;
    let lastValidIndex = -1;
    
    for (let i = 0; i < cleaned.length; i++) {
      const char = cleaned[i];
      
      if (escapeNext) {
        escapeNext = false;
        continue;
      }
      
      if (char === '\\') {
        escapeNext = true;
        continue;
      }
      
      if (char === '"' && !escapeNext) {
        inString = !inString;
      }
      
      if (!inString) {
        if (char === '{') bracketCount++;
        if (char === '}') {
          bracketCount--;
          if (bracketCount === 0) {
            lastValidIndex = i;
            break;
          }
        }
      }
    }
    
    if (lastValidIndex > 0) {
      cleaned = cleaned.substring(0, lastValidIndex + 1);
    }
    
    if (bracketCount > 0) {
      cleaned += '}'.repeat(bracketCount);
    }
    
    try {
      return JSON.parse(cleaned);
    } catch (e) {
      console.error('JSON parse error:', e);
      throw new Error('Failed to parse exam data');
    }
  };

  const gradeTheoryAnswers = async (answers: TheoryAnswer[], examState: ExamState | null, confidenceLevels: ('High' | 'Medium' | 'Low' | null)[]) => {
    if (!examState || !examState.questions) return { totalObtained: 0, feedback: [], enhancedFeedback: null };

    setIsLoading(true);
    
    try {
      const topics = examConfig.university.topics?.length
        ? examConfig.university.topics
        : [examConfig.university.course || 'Course Content'];
      
      const gradingPromises = answers.map(async (answer, qIndex) => {
        const question = examState.questions[qIndex];
        
        if (!answer.submitted) {
          return {
            qIndex,
            totalMarks: 0,
            subMarks: {},
            feedback: { subFeedback: {}, overall: "No answer submitted." },
            enhancedFeedback: null
          };
        }

        let subQuestionsPrompt = '';
        for (const sub of question.subQuestions) {
          subQuestionsPrompt += `
**Sub-question (${sub.label})** (${sub.marks} marks)
- Expected verb: ${sub.expectedVerb}
- Question: ${sub.text}
- Student's answer: ${answer.subAnswers[sub.label] || 'No answer provided'}
`;
        }

        const gradingPrompt = `
You are an expert examiner grading a Nigerian university theory exam.

**Course:** ${examConfig.university.course}
**Field of Study:** ${examConfig.university.fieldOfStudy}
**Topics Covered:** ${topics.join(', ')}

**Main Question ${question.mainQuestionNumber}** (Total: ${question.totalMarks} marks)

${subQuestionsPrompt}

**Model Answer Reference:**
${JSON.stringify(question.modelAnswer, null, 2)}

**Student's Confidence (overall):** ${confidenceLevels[qIndex] || 'Not rated'}

Please grade EACH sub-question individually.

Return ONLY valid JSON in this exact format:
{
  "subMarks": {
    "a": number,
    "b": number,
    "c": number,
    "d": number
  },
  "subFeedback": {
    "a": "specific feedback for sub-question a",
    "b": "specific feedback for sub-question b",
    "c": "specific feedback for sub-question c"
  },
  "overallFeedback": "brief overall feedback",
  "markingBreakdown": [
    {
      "subLabel": "a",
      "criteria": "criteria name",
      "maxScore": number,
      "scoreAwarded": number,
      "examinerExpectation": "what examiner expected",
      "userPerformance": "what student wrote",
      "marksLostReason": "why marks lost"
    }
  ],
  "missingPoints": ["missing point 1", "missing point 2"],
  "strongPoints": ["strong point 1"]
}`;

        const systemMessage = "You are an expert examiner for Nigerian university exams. Grade each sub-question separately based on the marking criteria. Be specific and constructive.";

        try {
          const response = await callOpenRouterApi(gradingPrompt, userProfile, systemMessage);
          const parsedResult = safeJsonParse(response);
          
          const totalMarks = Object.values(parsedResult.subMarks || {}).reduce((sum: number, val: number) => sum + val, 0);
          
          return {
            qIndex,
            totalMarks,
            subMarks: parsedResult.subMarks || {},
            feedback: {
              subFeedback: parsedResult.subFeedback || {},
              overall: parsedResult.overallFeedback || "Graded."
            },
            enhancedFeedback: {
              markingBreakdown: parsedResult.markingBreakdown || [],
              missingPoints: parsedResult.missingPoints || [],
              strongPoints: parsedResult.strongPoints || []
            }
          };
        } catch (parseError) {
          console.error('Error parsing grading response:', parseError);
          const defaultSubMarks: Record<string, number> = {};
          for (const sub of question.subQuestions) {
            defaultSubMarks[sub.label] = Math.round(sub.marks * 0.5);
          }
          return {
            qIndex,
            totalMarks: Object.values(defaultSubMarks).reduce((a, b) => a + b, 0),
            subMarks: defaultSubMarks,
            feedback: {
              subFeedback: {},
              overall: "Automatically graded - detailed feedback unavailable."
            },
            enhancedFeedback: null
          };
        }
      });

      const gradingResults = await Promise.all(gradingPromises);
      
      const totalObtained = gradingResults.reduce((sum, result) => sum + result.totalMarks, 0);
      const totalPossible = examState.questions.reduce((sum, q) => sum + q.totalMarks, 0);
      const percentage = totalPossible > 0 ? (totalObtained / totalPossible) * 100 : 0;
      
      const avgConfidence = confidenceLevels.filter(c => c !== null).length > 0
        ? confidenceLevels.reduce((acc, c) => {
            if (c === 'High') return acc + 100;
            if (c === 'Medium') return acc + 60;
            if (c === 'Low') return acc + 30;
            return acc + 50;
          }, 0) / confidenceLevels.filter(c => c !== null).length
        : 50;
      
      const readinessScore = Math.min(100, Math.max(0, Math.round(percentage * 0.7 + avgConfidence * 0.3)));
      
      let predictedOutcome: "Fail Risk" | "Borderline" | "Likely Pass";
      if (readinessScore >= 70) predictedOutcome = "Likely Pass";
      else if (readinessScore >= 50) predictedOutcome = "Borderline";
      else predictedOutcome = "Fail Risk";
      
      const allMissingPoints = gradingResults.flatMap(r => r.enhancedFeedback?.missingPoints || []);
      const allStrongPoints = gradingResults.flatMap(r => r.enhancedFeedback?.strongPoints || []);
      const allMarkingBreakdown = gradingResults.flatMap(r => r.enhancedFeedback?.markingBreakdown || []);
      
      const enhancedFeedback = {
        performance: {
          score: totalObtained,
          percentage: Math.round(percentage),
          readinessScore,
          predictedOutcome
        },
        markingBreakdown: allMarkingBreakdown,
        cognitiveAnalysis: {
          structure: 70,
          clarity: 65,
          conceptDepth: 60,
          blankRisk: 100 - Math.min(100, (totalObtained / totalPossible) * 100)
        },
        missingPoints: [...new Set(allMissingPoints)].slice(0, 8),
        strongPoints: [...new Set(allStrongPoints)].slice(0, 5),
        studyPlan: {
          focusAreas: examConfig.university.topics.slice(0, 5),
          actions: ["Review missed concepts", "Practice with model answers", "Focus on sub-questions where marks were lost"],
          estimatedImprovement: "15-25%"
        },
        retestPlan: {
          recommended: readinessScore < 70,
          instructions: readinessScore < 70 
            ? "Review the areas where you lost marks and retake this practice exam."
            : "Great job! Review the minor gaps identified."
        }
      };
      
      return {
        totalObtained,
        feedback: gradingResults.map(r => r.feedback),
        enhancedFeedback
      };

    } catch (error) {
      console.error('Error in grading:', error);
      return {
        totalObtained: 0,
        feedback: [],
        enhancedFeedback: null
      };
    } finally {
      setIsLoading(false);
    }
  };

  const calculateScore = useCallback(async (answers: TheoryAnswer[], examState: ExamState | null, confidenceLevels: ('High' | 'Medium' | 'Low' | null)[]) => {
    if (!examState) return;

    const gradingResults = await gradeTheoryAnswers(answers, examState, confidenceLevels);
    
    const totalPossible = examState.questions.reduce((sum, q) => sum + q.totalMarks, 0);
    const timeTaken = Math.round((Date.now() - examState.startTime.getTime()) / 1000 / 60);
    
    const ef = gradingResults.enhancedFeedback;
    const scorePercentage = Math.round(gradingResults.totalObtained/totalPossible*100);
    const updatedTitle = `${examConfig.university.course} - ${scorePercentage}%`;
    await updateSessionTitle(currentSessionId, updatedTitle);
    
    const updatedAnswers = examState.answers.map((answer, idx) => {
      const result = gradingResults.feedback[idx];
      return {
        ...answer,
        marks: {
          subMarks: result?.subMarks || {},
          total: result?.totalMarks || 0
        },
        feedback: result
      };
    });
    
    // Update exam state with results - THIS IS THE CRITICAL PART
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        answers: updatedAnswers,
        totalMarks: gradingResults.totalObtained,
        isComplete: true,
        endTime: new Date(),
        showResults: true,
        isGrading: false,
        performance: ef?.performance,
        markingBreakdown: ef?.markingBreakdown,
        cognitiveAnalysis: ef?.cognitiveAnalysis,
        missingPoints: ef?.missingPoints,
        strongPoints: ef?.strongPoints,
        studyPlan: ef?.studyPlan,
        retestPlan: ef?.retestPlan
      } : null);
      
      // Keep examStarted as true to prevent going back to config
      setExamStarted(true);
    }

    await updateExamSessionResults(currentSessionId, {
      exam_completed: true,
      score_percentage: scorePercentage,
      total_marks: totalPossible,
      obtained_marks: gradingResults.totalObtained,
      total_questions: examState.questions.length,
      time_taken_minutes: timeTaken,
      integrity_metrics: {
        copy_attempts: examIntegrity.copyAttempts,
        paste_attempts: examIntegrity.pasteAttempts,
        tab_switches: examIntegrity.tabSwitches,
        focus_lost_count: 0
      },
      answers: updatedAnswers,
      feedback_data: ef ? JSON.stringify(ef) : null
    });

    // Update session in sidebar without resetting UI
    setSessions(prev => prev.map(session => 
      session.id === currentSessionId 
        ? {
            ...session,
            title: updatedTitle,
            exam_completed: true,
            total_marks: totalPossible,
            obtained_marks: gradingResults.totalObtained,
            total_questions: examState.questions.length,
            time_taken_minutes: timeTaken,
            score_percentage: scorePercentage,
            answers: updatedAnswers,
            feedback_data: ef
          }
        : session
    ));

  }, [currentSessionId, examIntegrity, userProfile, examConfig.university.course, examConfig.university.topics]);

  // Timer effect
  useEffect(() => {
    if (examState && !examState.isComplete && timeLeft > 0 && !examState.isGrading) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      
      timerIntervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          setTimeLeft(prev => {
            if (prev <= 1) {
              if (examState && !examState.isComplete) {
                const updatedAnswers = examState.answers.map(answer => 
                  answer.submitted ? answer : { ...answer, submitted: true, submittedTime: new Date() }
                );
                
                setExamState(prevState => prevState ? {
                  ...prevState,
                  answers: updatedAnswers,
                  isComplete: true,
                  endTime: new Date(),
                  isGrading: true
                } : null);
                
                calculateScore(updatedAnswers, examState, confidenceLevels);
              }
              
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
              }
              
              return 0;
            }
            return prev - 1;
          });
        }
      }, 1000);
      
      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }
  }, [examState, timeLeft, calculateScore, confidenceLevels]);

  const handleConfigChange = (field: string, value: any) => {
    setExamConfig(prev => {
      const newConfig = {...prev};
      
      if (field.startsWith('university.')) {
        const subField = field.split('.')[1];
        newConfig.university = {...newConfig.university, [subField]: value};
      } else {
        (newConfig as any)[field] = value;
      }
      
      return newConfig;
    });
  };

  const handleQuestionCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempQuestionCount(value);
    
    if (value !== '') {
      const num = parseInt(value);
      if (!isNaN(num) && num > 0) {
        handleConfigChange('questionCount', num);
      }
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTempDuration(value);
    
    if (value !== '') {
      const num = parseInt(value);
      if (!isNaN(num) && num > 0) {
        handleConfigChange('duration', num);
      }
    }
  };

  const handleQuestionCountBlur = () => {
    if (tempQuestionCount === '') {
      const defaultValue = 4;
      setTempQuestionCount(defaultValue.toString());
      handleConfigChange('questionCount', defaultValue);
    }
  };

  const handleDurationBlur = () => {
    if (tempDuration === '') {
      const defaultValue = 120;
      setTempDuration(defaultValue.toString());
      handleConfigChange('duration', defaultValue);
    }
  };

  const validateConfig = () => {
    if (!examConfig.selectedCourseId) {
      setErrorMessage('Please select a course from your registered courses');
      return false;
    }
    if (!examConfig.university.fieldOfStudy?.trim()) {
      setErrorMessage('Field of Study is required');
      return false;
    }
    
    return true;
  };

  const handleStartExam = async () => {
    if (!validateConfig()) return;
    
    setIsLoading(true);
    setErrorMessage('');
    
    const newSession = await createNewSession();
    if (!newSession) {
      setIsLoading(false);
      return;
    }
    
    const title = `${examConfig.university.course} Theory Exam Practice`;
    await updateSessionTitle(newSession.id, title);
    
    const configMessage = `Starting ${examConfig.questionCount}-question theory exam for ${examConfig.university.course}...`;
    
    await saveChatMessage(newSession.id, 'user', configMessage);
    
    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: configMessage,
      timestamp: new Date(),
      chat_type: 'theory-exam',
    };

    const newMessages = [userMessage];
    if (isMountedRef.current) {
      setMessages(newMessages);
    }

    try {
      const courseContext = `
**Course Name:** ${examConfig.selectedCourse?.course_name}
**Course Code:** ${examConfig.selectedCourse?.course_code}
${examConfig.selectedCourse?.syllabus ? `**Syllabus/Outline:** ${examConfig.selectedCourse.syllabus}` : ''}
${examConfig.selectedCourse?.lecturer_keywords?.length ? `**Lecturer Keywords:** ${examConfig.selectedCourse.lecturer_keywords.join(', ')}` : ''}
**Field of Study:** ${examConfig.university.fieldOfStudy}
**Level:** ${examConfig.university.level}
${examConfig.university.topics?.length ? `**Topics:** ${examConfig.university.topics.join(', ')}` : ''}
`;
      
      const examPrompt = `
Generate a Nigerian university-style theory exam based on the following course information:

${courseContext}

**Configuration:**
- Number of Main Questions: ${examConfig.questionCount}
- Each main question MUST have sub-questions (a), (b), (c) - following cognitive progression
- Question Types: ${examConfig.university.questionTypes?.join(', ') || 'definition, explain, differentiate, with-examples'}

**REQUIRED STRUCTURE FOR EACH QUESTION:**
Each main question must have:
- (a): Definition or basic recall (20-30% of marks)
- (b): Expansion or explanation (25-35% of marks)  
- (c): Application, examples, or comparison (25-35% of marks)

Return ONLY valid JSON in this exact format:
{
  "questions": [
    {
      "mainQuestionNumber": 1,
      "subQuestions": [
        {"label": "a", "text": "Define [concept].", "marks": 4, "expectedVerb": "define"},
        {"label": "b", "text": "Explain the components of [concept].", "marks": 8, "expectedVerb": "explain"},
        {"label": "c", "text": "With two examples each, describe the types of [concept].", "marks": 8, "expectedVerb": "describe"}
      ],
      "totalMarks": 20,
      "questionType": "theory",
      "modelAnswer": {
        "subAnswers": {
          "a": "Model answer for (a)...",
          "b": "Model answer for (b)...",
          "c": "Model answer for (c)..."
        },
        "markingScheme": {
          "a": ["Key point 1 for marking", "Key point 2 for marking"],
          "b": ["Component 1 explanation", "Component 2 explanation"],
          "c": ["Example 1 quality", "Example 2 quality"]
        }
      }
    }
  ]
}`;

      const systemMessage = `You are a Theory Exam Generator for BB EdTech, specializing in Nigerian university exam format. Generate questions with proper (a), (b), (c) sub-structure. Use formal British English. Each sub-question must have marks in parentheses.`;
      
      const response = await callOpenRouterApi(examPrompt, userProfile, systemMessage);
      const examData = safeJsonParse(response);

      if (!examData.questions || examData.questions.length !== examConfig.questionCount) {
        throw new Error(`Failed to generate the requested number of questions.`);
      }

      const assistantContent = `## 📚 Nigerian University Theory Exam Started! ⏱️

**Course:** ${examConfig.university.course}
**Field:** ${examConfig.university.fieldOfStudy}
**Level:** ${examConfig.university.level}

You have **${examConfig.duration} minutes** to complete **${examConfig.questionCount}** main questions.

### Exam Structure:
Each main question has sub-questions **(a), (b), (c)** following a cognitive progression:
- **(a)** Basic recall / definition
- **(b)** Expansion / explanation  
- **(c)** Application / examples / comparison

### Instructions:
1. Answer each sub-question separately
2. ${showConfidenceCheck ? 'Rate your confidence after completing each main question' : ''}
3. Click "Save Answer" to save your progress
4. Submit when you're ready

**Good luck!** 🖋️`;
      
      await saveChatMessage(newSession.id, 'assistant', assistantContent);
      
      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date(),
        chat_type: 'theory-exam',
      };

      const updatedMessages = [...newMessages, assistantMessage];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }

      const initialAnswers: TheoryAnswer[] = examData.questions.map((q: any) => {
        const subAnswers: Record<string, string> = {};
        const subSubmitted: Record<string, boolean> = {};
        for (const sub of q.subQuestions) {
          subAnswers[sub.label] = '';
          subSubmitted[sub.label] = false;
        }
        return {
          subAnswers,
          subSubmitted,
          submitted: false
        };
      });

      if (isMountedRef.current) {
        setExamState({
          questions: examData.questions,
          currentQuestion: 0,
          currentSubQuestion: 'a',
          answers: initialAnswers,
          isComplete: false,
          totalMarks: 0,
          startTime: new Date(),
          endTime: null,
          showResults: false,
          isGrading: false
        });

        setConfidenceLevels(Array(examConfig.questionCount).fill(null));
        setTimeLeft(examConfig.duration * 60);
        setExamStarted(true);
      }

    } catch (error: any) {
      console.error('Error generating exam:', error);
      setErrorMessage(`Failed to generate exam: ${error.message || 'Please try again.'}`);
      
      const errorMsg: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Sorry, I encountered an error while generating the exam: ${error.message || 'Please try again.'}`,
        timestamp: new Date(),
        chat_type: 'theory-exam',
      };
      
      const updatedMessages = [...newMessages, errorMsg];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleConfidenceRating = (rating: 'High' | 'Medium' | 'Low') => {
    if (!examState || examState.isComplete) return;

    const newConfidenceLevels = [...confidenceLevels];
    newConfidenceLevels[examState.currentQuestion] = rating;

    if (isMountedRef.current) {
      setConfidenceLevels(newConfidenceLevels);
    }
  };

  const handleSubAnswerChange = (subLabel: string, answer: string) => {
    if (!examState || examState.isComplete) return;

    const newAnswers = [...examState.answers];
    newAnswers[examState.currentQuestion] = {
      ...newAnswers[examState.currentQuestion],
      subAnswers: {
        ...newAnswers[examState.currentQuestion].subAnswers,
        [subLabel]: answer
      }
    };

    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        answers: newAnswers
      } : null);
    }
  };

  const saveCurrentSubAnswer = (subLabel: string) => {
    if (!examState || examState.isComplete) return;

    const currentAnswer = examState.answers[examState.currentQuestion];
    const currentSubAnswer = currentAnswer.subAnswers[subLabel];
    
    if (!currentSubAnswer?.trim()) {
      setErrorMessage(`Please write an answer for sub-question (${subLabel}) before saving.`);
      return;
    }

    const newAnswers = [...examState.answers];
    newAnswers[examState.currentQuestion] = {
      ...newAnswers[examState.currentQuestion],
      subSubmitted: {
        ...newAnswers[examState.currentQuestion].subSubmitted,
        [subLabel]: true
      }
    };

    const currentQuestionData = examState.questions[examState.currentQuestion];
    const allSubsSaved = currentQuestionData.subQuestions.every(
      sub => newAnswers[examState.currentQuestion].subSubmitted[sub.label] === true
    );

    if (allSubsSaved) {
      newAnswers[examState.currentQuestion] = {
        ...newAnswers[examState.currentQuestion],
        submitted: true,
        submittedTime: new Date()
      };
    }

    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        answers: newAnswers
      } : null);
    }
    
    setErrorMessage('');
  };

  const navigateToSubQuestion = (subLabel: string) => {
    if (!examState || examState.isComplete) return;
    
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        currentSubQuestion: subLabel
      } : null);
    }
  };

  const navigateToQuestion = (index: number) => {
    if (!examState || examState.isComplete) return;
    
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        currentQuestion: index,
        currentSubQuestion: 'a'
      } : null);
    }
  };

  const goToPreviousQuestion = () => {
    if (!examState || examState.currentQuestion === 0) return;
    
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        currentQuestion: prev.currentQuestion - 1,
        currentSubQuestion: 'a'
      } : null);
    }
  };

  const goToNextQuestion = () => {
    if (!examState || examState.currentQuestion === examState.questions.length - 1) return;
    
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        currentSubQuestion: 'a'
      } : null);
    }
  };

  const handleSubmitExam = async () => {
    if (!examState || isLoading) return;
    
    const unsavedMainQuestions = examState.answers.filter(a => !a.submitted && 
      Object.values(a.subAnswers).some(ans => ans.trim() !== '')
    );
    
    if (unsavedMainQuestions.length > 0) {
      if (!window.confirm(`You have ${unsavedMainQuestions.length} main questions with unsaved sub-answers. Do you want to submit anyway?`)) {
        return;
      }
    }
    
    if (isMountedRef.current) {
      setIsLoading(true);
      setExamState(prev => prev ? { ...prev, isGrading: true } : null);
    }
    
    const finalAnswers = examState.answers.map(answer => {
      const hasAnyAnswer = Object.values(answer.subAnswers).some(ans => ans.trim() !== '');
      if (hasAnyAnswer && !answer.submitted) {
        return {
          ...answer,
          submitted: true,
          subSubmitted: Object.keys(answer.subAnswers).reduce((acc, key) => {
            acc[key] = answer.subAnswers[key].trim() !== '' ? true : answer.subSubmitted[key];
            return acc;
          }, {} as Record<string, boolean>),
          submittedTime: new Date()
        };
      }
      return answer;
    });
    
    if (isMountedRef.current) {
      setExamState(prev => prev ? {
        ...prev,
        answers: finalAnswers,
        isComplete: true,
        endTime: new Date(),
        isGrading: true
      } : null);
    }
    
    await calculateScore(finalAnswers, examState, confidenceLevels);
  };

  const handleTakeAnotherPractice = async () => {
    // Reset all exam states
    setExamState(null);
    setExamStarted(false);
    setConfidenceLevels([]);
    setShowCorrectAnswers(false);
    setTimeLeft(0);
    setErrorMessage('');
    
    // Clear timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Create a new session
    await createNewSession();
  };

  const handleSeeCorrectAnswers = () => {
    setShowCorrectAnswers(true);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateAnsweredCount = () => {
    if (!examState) return 0;
    let answered = 0;
    for (const answer of examState.answers) {
      const hasAnyAnswer = Object.values(answer.subAnswers).some(ans => ans.trim() !== '');
      if (hasAnyAnswer) answered++;
    }
    return answered;
  };

  const calculateSavedCount = () => {
    if (!examState) return 0;
    return examState.answers.filter(a => a.submitted).length;
  };

  const switchToSession = async (sessionId: string) => {
    // Prevent recursive switching
    if (isSwitchingSessionRef.current) return;
    isSwitchingSessionRef.current = true;
    
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      // Clear any existing timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      if (isMountedRef.current) {
        setCurrentSessionId(sessionId);
        setMessages(session.messages);
        setShowCorrectAnswers(false);
        setErrorMessage('');
        
        if (session.config) {
          setExamConfig(session.config);
        }
        
        // Check if session has completed exam with feedback
        if (session.feedback_data && session.exam_completed && session.answers) {
          // Reconstruct exam state from saved data
          const totalQuestions = session.total_questions || 0;
          
          // Try to get questions from config or create placeholder
          let reconstructedQuestions: TheoryQuestion[] = [];
          
          // If we have stored questions in config, use them
          if (session.config?.university?.generatedQuestions) {
            reconstructedQuestions = session.config.university.generatedQuestions;
          } else {
            // Create placeholder questions based on answers
            reconstructedQuestions = Array(totalQuestions).fill(null).map((_, idx) => ({
              mainQuestionNumber: idx + 1,
              subQuestions: session.answers?.[idx]?.subAnswers 
                ? Object.keys(session.answers[idx].subAnswers).map(label => ({
                    label,
                    text: `Question (${label}) - Review your answer below`,
                    marks: 5,
                    expectedVerb: 'explain' as const
                  }))
                : [{ label: 'a', text: 'Question text not available', marks: 10, expectedVerb: 'explain' as const }],
              totalMarks: session.total_marks ? Math.ceil(session.total_marks / totalQuestions) : 20,
              questionType: 'theory',
              modelAnswer: { subAnswers: {}, markingScheme: {} }
            }));
          }
          
          const reconstructedExamState: ExamState = {
            questions: reconstructedQuestions,
            currentQuestion: 0,
            currentSubQuestion: 'a',
            answers: session.answers,
            isComplete: true,
            totalMarks: session.obtained_marks || 0,
            startTime: session.timestamp,
            endTime: new Date(),
            showResults: true,
            isGrading: false,
            performance: session.feedback_data?.performance,
            markingBreakdown: session.feedback_data?.markingBreakdown,
            cognitiveAnalysis: session.feedback_data?.cognitiveAnalysis,
            missingPoints: session.feedback_data?.missingPoints,
            strongPoints: session.feedback_data?.strongPoints,
            studyPlan: session.feedback_data?.studyPlan,
            retestPlan: session.feedback_data?.retestPlan
          };
          
          setExamState(reconstructedExamState);
          setExamStarted(true);
        } else {
          // Incomplete session - show config
          setExamState(null);
          setExamStarted(false);
        }
      }
    }
    
    // Reset the switching flag after a delay
    setTimeout(() => {
      isSwitchingSessionRef.current = false;
    }, 500);
  };

  const deleteSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session?.isTemporary) {
      if (isMountedRef.current) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    } else {
      await supabase.from('exam_messages').delete().eq('session_id', sessionId);
      await supabase.from('exam_sessions').delete().eq('id', sessionId);
      if (isMountedRef.current) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    }

    if (currentSessionId === sessionId) {
      const remainingSessions = sessions.filter(s => s.id !== sessionId);
      if (remainingSessions.length > 0) {
        switchToSession(remainingSessions[0].id);
      } else {
        setCurrentSessionId('');
        setMessages([]);
        setExamStarted(false);
        setExamState(null);
      }
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim() || isLoading) return;
    
    if (!currentSessionId) {
      const newSession = await createNewSession();
      if (!newSession) {
        setErrorMessage('Please start an exam or create a session first');
        return;
      }
    }
    
    if (isMountedRef.current) {
      setIsLoading(true);
    }
    setErrorMessage('');
    
    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: chatInput,
      timestamp: new Date(),
      chat_type: 'theory-exam',
    };
    
    const newMessages = [...messages, userMessage];
    if (isMountedRef.current) {
      setMessages(newMessages);
    }
    await saveChatMessage(currentSessionId, 'user', chatInput);
    setChatInput('');
    
    try {
      const systemMessage = "You are a helpful assistant for BB EdTech's Theory Exam Simulator. Provide detailed feedback on answers, explain concepts, and help improve writing skills. Use proper Markdown formatting and LaTeX for mathematical expressions.";
      
      const response = await callOpenRouterApi(chatInput, userProfile, systemMessage);
      
      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
        chat_type: 'theory-exam',
      };
      
      if (isMountedRef.current) {
        setMessages(prev => [...prev, assistantMessage]);
      }
      await saveChatMessage(currentSessionId, 'assistant', response);
      
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Failed to get response. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const renderCourseSelection = () => {
    return (
      <div className="form-group full-width">
        <label>Select Registered Course *</label>
        <select
          value={examConfig.selectedCourseId || ''}
          onChange={(e) => handleCourseSelect(e.target.value)}
          className="form-input"
          disabled={loadingCourses}
        >
          <option value="">-- Select a course --</option>
          {userCourses.map(course => (
            <option key={course.id} value={course.id}>
              {course.course_code} - {course.course_name}
            </option>
          ))}
        </select>
        {loadingCourses && <div className="form-hint">Loading your courses...</div>}
        {userCourses.length === 0 && !loadingCourses && (
          <div className="form-hint warning">
            No courses found. Please add courses in the Course Registration page first.
          </div>
        )}
        {examConfig.selectedCourse && (
          <div className="selected-course-info">
            <div className="course-detail">
              <strong>Course:</strong> {examConfig.selectedCourse.course_name}
            </div>
            {examConfig.selectedCourse.lecturer_keywords?.length > 0 && (
              <div className="course-keywords-preview">
                <strong>Keywords:</strong> {examConfig.selectedCourse.lecturer_keywords.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderConfigDetails = () => {
    return (
      <div className="config-step">
        <div className="step-header">
          <h3>Configure Your Nigerian University Theory Exam</h3>
          <p className="step-description">Select a course and configure your exam preferences</p>
        </div>
        
        <div className="config-form">
          {renderCourseSelection()}
          
          <div className="form-row">
            <div className="form-group">
              <label>Field of Study *</label>
              <input
                type="text"
                value={examConfig.university.fieldOfStudy}
                onChange={(e) => handleConfigChange('university.fieldOfStudy', e.target.value)}
                placeholder="e.g., Computer Science, Medicine, Business"
                className="form-input"
                disabled={!examConfig.selectedCourse}
              />
            </div>
            
            <div className="form-group">
              <label>Level</label>
              <select
                value={examConfig.university.level}
                onChange={(e) => handleConfigChange('university.level', e.target.value as any)}
                className="form-input"
                disabled={!examConfig.selectedCourse}
              >
                <option value="Undergraduate">Undergraduate</option>
                <option value="Postgraduate">Postgraduate</option>
                <option value="Researcher">Researcher</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Number of Questions</label>
              <input
                type="number"
                min="1"
                max="10"
                value={tempQuestionCount}
                onChange={handleQuestionCountChange}
                onBlur={handleQuestionCountBlur}
                placeholder="4"
                className="form-input"
              />
              <div className="form-hint">Each question will have sub-questions (a), (b), (c)</div>
            </div>
            
            <div className="form-group">
              <label>Duration (minutes)</label>
              <input
                type="number"
                min="1"
                value={tempDuration}
                onChange={handleDurationChange}
                onBlur={handleDurationBlur}
                placeholder="120"
                className="form-input"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={showConfidenceCheck}
                  onChange={(e) => setShowConfidenceCheck(e.target.checked)}
                />
                <span>Enable confidence tracking (rate after each question)</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="config-navigation">
          <button
            className="start-exam-btn"
            onClick={handleStartExam}
            disabled={isLoading || !examConfig.selectedCourseId}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Creating Theory Exam...
              </>
            ) : (
              `📝 Start Theory Practice`
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderConfidenceRating = () => {
    if (!showConfidenceCheck || !examState || examState.isComplete) return null;
    
    const currentConfidence = confidenceLevels[examState.currentQuestion];
    
    return (
      <div className="confidence-rating-container">
        <div className="confidence-prompt">
          <span className="confidence-icon">🤔</span>
          <span className="confidence-label">How confident are you in your answers for this main question?</span>
        </div>
        <div className="confidence-buttons">
          <button
            className={`confidence-btn high ${currentConfidence === 'High' ? 'active' : ''}`}
            onClick={() => handleConfidenceRating('High')}
          >
            <span className="confidence-emoji">😊</span>
            <span>High</span>
          </button>
          <button
            className={`confidence-btn medium ${currentConfidence === 'Medium' ? 'active' : ''}`}
            onClick={() => handleConfidenceRating('Medium')}
          >
            <span className="confidence-emoji">😐</span>
            <span>Medium</span>
          </button>
          <button
            className={`confidence-btn low ${currentConfidence === 'Low' ? 'active' : ''}`}
            onClick={() => handleConfidenceRating('Low')}
          >
            <span className="confidence-emoji">😰</span>
            <span>Low</span>
          </button>
        </div>
      </div>
    );
  };

  const renderSubQuestionNav = () => {
    if (!examState || examState.isComplete) return null;
    
    const currentQ = examState.questions[examState.currentQuestion];
    const currentAnswer = examState.answers[examState.currentQuestion];
    
    return (
      <div className="sub-question-nav">
        {currentQ.subQuestions.map((sub) => {
          const isSaved = currentAnswer.subSubmitted[sub.label];
          const isActive = examState.currentSubQuestion === sub.label;
          const hasAnswer = currentAnswer.subAnswers[sub.label]?.trim() !== '';
          
          return (
            <button
              key={sub.label}
              className={`sub-nav-btn ${isActive ? 'active' : ''} ${isSaved ? 'saved' : ''} ${hasAnswer ? 'has-answer' : ''}`}
              onClick={() => navigateToSubQuestion(sub.label)}
            >
              ({sub.label}) {sub.marks} marks
              {isSaved && <span className="saved-check">✓</span>}
            </button>
          );
        })}
      </div>
    );
  };

  const renderCurrentSubQuestion = () => {
    if (!examState) return null;
    
    const currentQ = examState.questions[examState.currentQuestion];
    const currentSub = currentQ.subQuestions.find(s => s.label === examState.currentSubQuestion);
    const currentAnswer = examState.answers[examState.currentQuestion];
    
    if (!currentSub) return null;
    
    const isSaved = currentAnswer.subSubmitted[currentSub.label];
    const currentSubAnswer = currentAnswer.subAnswers[currentSub.label] || '';
    
    return (
      <div className="sub-question-container">
        <div className="sub-question-header">
          <span className="sub-question-label">({currentSub.label})</span>
          <span className="sub-question-marks">{currentSub.marks} marks</span>
          <span className="sub-question-verb">{currentSub.expectedVerb.toUpperCase()}</span>
        </div>
        
        <div className="sub-question-text">
          <MarkdownRenderer content={currentSub.text} theme={theme} />
        </div>
        
        {examState.isComplete ? (
          <div className="graded-answer">
            <div className="answer-content">
              <MarkdownRenderer content={currentSubAnswer || "No answer submitted"} theme={theme} />
            </div>
            {currentAnswer.marks?.subMarks?.[currentSub.label] !== undefined && (
              <div className="answer-feedback">
                <div className="marks-awarded">
                  <strong>Marks:</strong> {currentAnswer.marks.subMarks[currentSub.label]}/{currentSub.marks}
                </div>
                <div className="feedback-text">
                  <strong>Feedback:</strong> {currentAnswer.feedback?.subFeedback?.[currentSub.label] || "No specific feedback"}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <textarea
              className="theory-answer-input"
              value={currentSubAnswer}
              onChange={(e) => handleSubAnswerChange(currentSub.label, e.target.value)}
              placeholder={`Write your answer for sub-question (${currentSub.label}) here... Use Markdown formatting and LaTeX for mathematical expressions.`}
              disabled={examState.isComplete}
              rows={8}
            />
            <div className="answer-tools">
              <div className="word-count">
                Words: {currentSubAnswer.split(/\s+/).filter(word => word.length > 0).length || 0}
              </div>
              <div className="answer-status">
                {isSaved ? (
                  <span className="status-saved">✓ Saved</span>
                ) : (
                  <span className="status-unsaved">○ Unsaved</span>
                )}
              </div>
            </div>
            {!isSaved && currentSubAnswer.trim() && (
              <button
                className="save-answer-btn"
                onClick={() => saveCurrentSubAnswer(currentSub.label)}
              >
                💾 Save Sub-Answer ({currentSub.label})
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  const renderQuestionBoxes = () => {
    if (!examState || examState.isComplete) return null;
    
    return (
      <div className="question-boxes-container">
        <h4>Main Questions</h4>
        <div className="question-boxes-grid">
          {examState.questions.map((q, index) => {
            const isAnswered = Object.values(examState.answers[index]?.subAnswers || {}).some(ans => ans.trim() !== '');
            const isFullySaved = examState.answers[index]?.submitted;
            const isCurrent = examState.currentQuestion === index;
            const confidence = confidenceLevels[index];
            
            let boxClass = 'question-box';
            if (isCurrent) boxClass += ' current';
            if (isFullySaved) boxClass += ' fully-saved';
            else if (isAnswered) boxClass += ' partially-answered';
            else boxClass += ' unanswered';
            if (confidence === 'High') boxClass += ' high-confidence';
            if (confidence === 'Medium') boxClass += ' medium-confidence';
            if (confidence === 'Low') boxClass += ' low-confidence';
            
            const savedCount = Object.values(examState.answers[index]?.subSubmitted || {}).filter(v => v === true).length;
            const totalSubs = q.subQuestions.length;
            
            return (
              <button
                key={index}
                className={boxClass}
                onClick={() => navigateToQuestion(index)}
                disabled={examState.isComplete}
                title={`${savedCount}/${totalSubs} sub-questions saved | Confidence: ${confidence || 'Not rated'}`}
              >
                {index + 1}
                <div className="sub-progress">
                  {savedCount}/{totalSubs}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTimer = () => {
    if (!examStarted || !timeLeft || !examState || examState.isComplete || examState.isGrading) return null;
    
    const totalSeconds = examConfig.duration * 60;
    const percentage = (timeLeft / totalSeconds) * 100;
    const isWarning = timeLeft < 600;
    
    return (
      <div className="exam-timer-section">
        <div className="timer-spinner">
          <svg className="timer-svg" viewBox="0 0 100 100">
            <circle className="timer-background" cx="50" cy="50" r="45"></circle>
            <circle 
              className={`timer-progress ${isWarning ? 'warning' : ''}`}
              cx="50" cy="50" r="45"
              strokeDasharray="283"
              strokeDashoffset={283 - (283 * percentage / 100)}
            ></circle>
          </svg>
          <div className="timer-text">
            <div className="timer-digits">{formatTime(timeLeft)}</div>
            <div className="timer-label">Time Left</div>
          </div>
        </div>
      </div>
    );
  };

  const renderProgressStats = () => {
    if (!examState || examState.isComplete || examState.isGrading) return null;
    
    const answeredCount = calculateAnsweredCount();
    const savedCount = calculateSavedCount();
    const totalQuestions = examState.questions.length;
    
    let totalSubs = 0;
    let savedSubs = 0;
    for (let i = 0; i < examState.questions.length; i++) {
      totalSubs += examState.questions[i].subQuestions.length;
      savedSubs += Object.values(examState.answers[i]?.subSubmitted || {}).filter(v => v === true).length;
    }
    
    return (
      <div className="progress-stats">
        <div className="stat-item">
          <strong>Questions</strong>
          <span>{answeredCount}/{totalQuestions}</span>
        </div>
        <div className="stat-item">
          <strong>Sub-Answers</strong>
          <span>{savedSubs}/{totalSubs}</span>
        </div>
        <div className="stat-item">
          <strong>Confidence</strong>
          <span>{confidenceLevels.filter(c => c !== null).length}</span>
        </div>
        <div className="stat-item">
          <strong>Integrity</strong>
          <span>{examIntegrity.tabSwitches}</span>
        </div>
      </div>
    );
  };

  const renderMainContent = () => {
    // Show results if exam is complete and has results
    if (examState?.showResults && examState?.isComplete && examState?.performance) {
      return renderResultsInterface();
    }
    
    // Show grading in progress
    if (examState?.isGrading) {
      return (
        <div className="grading-in-progress">
          <div className="grading-spinner"></div>
          <h3>📝 Grading Your Answers</h3>
          <p>Your answers are being analyzed by our AI examiner...</p>
          <p className="grading-note">This may take a few moments. Please don't close this page.</p>
        </div>
      );
    }
    
    // Show active exam
    if (examStarted && examState && !examState.isComplete) {
      return renderExamInterface();
    }
    
    // Show config
    return renderConfigDetails();
  };
  
  const renderResultsInterface = () => {
    if (!examState || !examState.performance) return null;
    
    return (
      <div className="exam-results-container">
        <FeedbackDashboard
          performance={examState.performance}
          markingBreakdown={examState.markingBreakdown}
          cognitiveAnalysis={examState.cognitiveAnalysis}
          missingPoints={examState.missingPoints}
          strongPoints={examState.strongPoints}
          studyPlan={examState.studyPlan}
          retestPlan={examState.retestPlan}
          questions={examState.questions.map(q => ({
            question: q.subQuestions.map(s => `(${s.label}) ${s.text}`).join('\n\n'),
            questionType: 'essay' as const,
            maxMarks: q.totalMarks,
            estimatedTime: 15,
            markingCriteria: [],
            modelAnswer: Object.entries(q.modelAnswer.subAnswers).map(([label, ans]) => `(${label}) ${ans}`).join('\n\n')
          }))}
          answers={examState.answers.map(a => ({
            answer: Object.entries(a.subAnswers).map(([label, ans]) => `(${label}) ${ans}`).join('\n\n'),
            submitted: a.submitted,
            submittedTime: a.submittedTime,
            marks: a.marks?.total,
            feedback: a.feedback?.overall
          }))}
          theme={theme}
          onTakeAnother={handleTakeAnotherPractice}
          onSeeCorrectAnswers={handleSeeCorrectAnswers}
          onChat={() => {
            chatContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
        />
        
        <div className="exam-chat-interface">
          <div className="chat-messages-container" ref={chatContainerRef}>
            {messages.map((message) => (
              <div key={message.id} className="message-wrapper">
                <ChatMessage 
                  message={message}
                  onSpeak={(text) => speak(text, message.id)}
                  isSpeaking={isMessageSpeaking(message.id)}
                  onShare={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/exam/message/${message.id}`);
                  }}
                />
              </div>
            ))}
            {isLoading && (
              <div className="typing-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="typing-text">Processing...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="chat-input-container">
            <InputField
              value={chatInput}
              onChange={setChatInput}
              onSubmit={handleChatSubmit}
              loading={isLoading}
              placeholder="Ask for clarification on feedback, request additional examples, or discuss your answers..."
            />
          </div>
        </div>
      </div>
    );
  };

  function renderExamInterface() {
    if (!examState) return null;
    
    const currentQ = examState.questions[examState.currentQuestion];
    const isFullySaved = examState.answers[examState.currentQuestion]?.submitted;
    
    return (
      <>
        <div className="exam-header">
          {renderTimer()}
          {renderProgressStats()}
        </div>
        
        <div className="exam-content-wrapper">
          <div className="exam-question-container">
            <div className="main-question-header">
              <h2>Question {examState.currentQuestion + 1} of {examState.questions.length}</h2>
              <div className="total-marks-badge">Total: {currentQ.totalMarks} marks</div>
              {isFullySaved && (
                <div className="fully-saved-badge">✓ All sub-answers saved</div>
              )}
            </div>
            
            {renderSubQuestionNav()}
            {renderCurrentSubQuestion()}
            {renderConfidenceRating()}
            
            <div className="exam-navigation">
              <button
                className="nav-btn prev-btn"
                onClick={goToPreviousQuestion}
                disabled={examState.currentQuestion === 0}
              >
                ← Previous Question
              </button>
              
              <button
                className="save-all-btn"
                onClick={() => {
                  const currentAnswers = examState.answers[examState.currentQuestion];
                  for (const sub of currentQ.subQuestions) {
                    if (currentAnswers.subAnswers[sub.label]?.trim() && !currentAnswers.subSubmitted[sub.label]) {
                      saveCurrentSubAnswer(sub.label);
                    }
                  }
                  alert('All unsaved sub-answers for this question have been saved!');
                }}
              >
                💾 Save All Sub-Answers
              </button>
              
              <button
                className="nav-btn next-btn"
                onClick={goToNextQuestion}
                disabled={examState.currentQuestion === examState.questions.length - 1}
              >
                Next Question →
              </button>
            </div>
            
            <div className="exam-submit-section">
              <button
                className="submit-exam-btn"
                onClick={handleSubmitExam}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner"></span>
                    Processing...
                  </>
                ) : (
                  '📤 Submit Exam for Grading'
                )}
              </button>
              <div className="submit-note">
                {examState.answers.filter(a => !a.submitted && 
                  Object.values(a.subAnswers).some(ans => ans.trim())
                ).length} questions with unsaved answers | 
                Auto-submit in {formatTime(timeLeft)}
              </div>
            </div>
          </div>
          
          {renderQuestionBoxes()}
        </div>
      </>
    );
  }

  const renderSidebar = () => (
    <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <div className="logo">📝</div>
        {sidebarOpen && <span className="app-name">Theory Exams</span>}
        <div className="sidebar-header-controls">
          <button
            className="theme-toggle-sidebar"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          {sidebarOpen && (
            <button
              className="sidebar-toggle-inside"
              onClick={() => setSidebarOpen(false)}
              title="Close sidebar"
            >
              ◀
            </button>
          )}
        </div>
      </div>
      
      {sidebarOpen && (
        <>
          <div className="user-profile-sidebar">
            <div className="user-avatar">
              {userProfile.name?.charAt(0) || 'U'}
            </div>
            <div className="user-info">
              <div className="user-name" title={userProfile.name}>
                {userProfile.name}
              </div>
              <div className="user-level">{userProfile.level || 'Beginner'}</div>
            </div>
          </div>
          
          <button className="new-exam-btn" onClick={createNewSession}>
            ➕ New Theory Exam
          </button>
          
          <div className="search-container">
            <input
              type="text"
              placeholder="Search theory exams..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="exam-history">
            <h4>Theory Exam History</h4>
            {sessions.length === 0 ? (
              <div className="no-sessions-message">
                No theory exams yet. Click "New Theory Exam" to get started!
              </div>
            ) : (
              sessions
                .filter(session => 
                  session.title.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((session) => (
                  <div
                    key={session.id}
                    className={`exam-session-item ${currentSessionId === session.id ? 'active' : ''}`}
                    onClick={() => switchToSession(session.id)}
                  >
                    <div className="session-title">
                      {session.exam_completed && session.score_percentage !== undefined && (
                        <span className={`score-indicator ${session.score_percentage >= 70 ? 'pass' : 'fail'}`}>
                          {session.score_percentage}%
                        </span>
                      )}
                      <span className="session-title-text">{session.title}</span>
                    </div>
                    <div className="session-info">
                      <span className="session-timestamp">
                        {new Date(session.timestamp).toLocaleDateString()}
                      </span>
                      <span className="exam-level-badge university">
                        University
                      </span>
                    </div>
                    <button
                      className="delete-session-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSession(session.id);
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                ))
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={`theory-exam-simulator ${theme}`}>
      <div className="background-container">
        {theme === 'light' ? (
          <div className="morning-sky"></div>
        ) : (
          <div className="night-sky"></div>
        )}
      </div>

      <div className="theory-exam-layout">
        {renderSidebar()}

        <div className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
          {!sidebarOpen && (
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              title="Open sidebar"
            >
              ▶
            </button>
          )}

          <div className="exam-header-section">
            <h1 className="exam-main-title">📝 Theory Exam Simulator</h1>
            <p className="exam-subtitle">Practice structured questions and get AI-powered feedback</p>
          </div>

          {errorMessage && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{errorMessage}</span>
            </div>
          )}

          <div className="exam-main-area" key={`theory-content-${examStarted}-${currentSessionId}`}>
            {renderMainContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheoryExamSimulator;